import { isDesktop } from './breakpoints'

const COOKIE = 'sidebar_collapsed'
// Slower than this (px per ms) the pointer is no longer considered to be heading for the flyout
const MIN_SPEED = 0.1
// Time (ms) the pointer may rest on another entry before that one takes over the flyout
const DWELL = 120

// Collapsing the sidebar is a desktop feature (isDesktop); the offcanvas menu of small screens always shows everything.

const isTouch = (event) => event.pointerType === 'touch' || event.pointerType === 'pen'

// Shortest distance from a point to a rectangle, 0 inside of it
function distanceToRect(x, y, rect) {
  const dx = Math.max(rect.left - x, 0, x - rect.right)
  const dy = Math.max(rect.top - y, 0, y - rect.bottom)
  return Math.hypot(dx, dy)
}

// Collapsible sidebar: the toggle button remembers its state in a cookie the layout reads while rendering.
// In the collapsed state entries with sub entries show them in a flyout while hovered. The flyout stays open
// while the pointer quickly gets closer to it, so it can be reached on a diagonal path without dropping out.
// Moving slowly, away or straight along the entries, or resting on another entry, hands over to that entry.
// Touch screens have no hover, so a tap opens the flyout instead of following the link.
export function initSidebar() {
  const sidebar = document.querySelector('.app-sidebar')
  const toggle = sidebar?.querySelector('[data-sidebar-toggle]')
  if (!sidebar || !toggle) return

  const collapsed = () => sidebar.classList.contains('app-sidebar-collapsed')
  const flyoutOf = (item) => item?.querySelector(':scope > .app-subnav')
  const topLevelItem = (target) => target.closest?.('#navbar > .nav > .nav-item')
  let lastDistance = Infinity
  let lastSample = null
  let hoveredNow = null
  let dwellItem = null
  let dwellTimer = null

  function clearDwell() {
    clearTimeout(dwellTimer)
    dwellItem = null
  }

  // Pointer speed in px per ms since the previous move
  function speedOf(event) {
    const now = Date.now()
    const previous = lastSample
    lastSample = { x: event.clientX, y: event.clientY, time: now }
    if (!previous) return Infinity
    return Math.hypot(event.clientX - previous.x, event.clientY - previous.y) / Math.max(now - previous.time, 1)
  }

  function startDwell(item) {
    if (dwellItem === item) return
    clearTimeout(dwellTimer)
    dwellItem = item
    dwellTimer = setTimeout(() => {
      dwellItem = null
      if (hoveredNow !== item) return
      closeFlyout()
      if (flyoutOf(item)) openFlyout(item)
    }, DWELL)
  }

  function closeFlyout() {
    sidebar.querySelectorAll('.nav-item.open').forEach((item) => {
      item.classList.remove('open')
      const flyout = flyoutOf(item)
      flyout.style.top = ''
      flyout.style.left = ''
      // The title only belongs to the flyout, the expanded sidebar names its entries itself
      flyout.querySelector('.app-flyout-title')?.remove()
    })
    lastDistance = Infinity
    clearDwell()
  }

  function openFlyout(item) {
    closeFlyout()
    const flyout = flyoutOf(item)
    const title = document.createElement('li')
    title.className = 'app-flyout-title'
    title.textContent = item.querySelector(':scope > .nav-link .nav-label')?.textContent || ''
    flyout.prepend(title)
    const rect = item.getBoundingClientRect()
    flyout.style.top = `${rect.top}px`
    flyout.style.left = `${rect.right + 4}px`
    item.classList.add('open')
  }

  function sync() {
    const label = collapsed() ? toggle.dataset.labelExpand : toggle.dataset.labelCollapse
    toggle.title = label
    toggle.setAttribute('aria-label', label)
    toggle.setAttribute('aria-expanded', String(!collapsed()))
  }

  toggle.addEventListener('click', () => {
    closeFlyout()
    sidebar.classList.toggle('app-sidebar-collapsed')
    document.cookie = `${COOKIE}=${collapsed() ? 1 : 0}; path=/; max-age=31536000; SameSite=Lax`
    sync()
  })

  // Entries with sub entries are expanded by clicking their name or their chevron instead of following
  // the link, their first sub entry leads to the same page. Only the collapsed sidebar of large screens
  // works differently: the sub entries show in a flyout and the entry is an ordinary link.
  function toggleExpanded(item) {
    const expanded = item.classList.toggle('expanded')
    item.querySelector(':scope > .nav-toggle')?.setAttribute('aria-expanded', String(expanded))
  }

  sidebar.addEventListener('click', (event) => {
    const button = event.target.closest('.nav-toggle')
    if (button) return toggleExpanded(button.parentElement)

    const link = event.target.closest('.nav-link')
    const item = link?.parentElement
    if (!item?.matches('.nav-item.has-sub') || !flyoutOf(item) || (collapsed() && isDesktop())) return

    event.preventDefault()
    toggleExpanded(item)
  })

  document.addEventListener('pointermove', (event) => {
    if (!collapsed() || !isDesktop() || isTouch(event)) return

    const speed = speedOf(event)
    const open = sidebar.querySelector('.nav-item.open')
    const hovered = sidebar.contains(event.target) ? topLevelItem(event.target) : null
    hoveredNow = hovered

    if (open && hovered === open) {
      lastDistance = Infinity
      clearDwell()
      return
    }

    if (open) {
      const distance = distanceToRect(event.clientX, event.clientY, flyoutOf(open).getBoundingClientRect())
      const approaching = distance < lastDistance - 0.5
      lastDistance = Math.min(distance, lastDistance)

      if (approaching && speed >= MIN_SPEED) {
        if (hovered) startDwell(hovered)
        else clearDwell()
        return
      }
      if (approaching && !hovered) return

      closeFlyout()
    }

    if (flyoutOf(hovered)) openFlyout(hovered)
  })

  document.documentElement.addEventListener('mouseleave', closeFlyout)

  sidebar.addEventListener('focusin', (event) => {
    const item = topLevelItem(event.target)
    if (collapsed() && isDesktop() && flyoutOf(item) && !item.classList.contains('open')) openFlyout(item)
  })
  sidebar.addEventListener('focusout', (event) => {
    const item = topLevelItem(event.target)
    if (item?.classList.contains('open') && !item.contains(event.relatedTarget) && !item.matches(':hover')) closeFlyout()
  })

  sidebar.addEventListener('click', (event) => {
    if (!collapsed() || !isDesktop() || !isTouch(event)) return

    const link = event.target.closest('.nav-link')
    const item = link?.parentElement
    if (!item?.matches('.nav-item') || !flyoutOf(item)) return

    event.preventDefault()
    if (item.classList.contains('open')) closeFlyout()
    else openFlyout(item)
  })

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.nav-item.open')) closeFlyout()
  })
  // Claimed, so that a board's panel stays open while it is only the flyout that closes
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !sidebar.querySelector('.nav-item.open')) return

    event.preventDefault()
    closeFlyout()
  })
  window.addEventListener('resize', closeFlyout)

  sync()
}
